<template>
  <div class="relative isolate overflow-hidden bg-white">
    <div
      class="flex w-full flex-col items-center gap-12 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:py-16 lg:pl-[max(80px,calc((100vw-1440px)/2+80px))] lg:pr-0"
    >
      <!-- Copy -->
      <div class="flex w-full flex-col items-start justify-center gap-10 lg:w-[624px] lg:shrink-0 lg:pl-8">
        <div class="flex flex-col items-start gap-4">
          <span class="rounded-fullpy-1 inline-flex items-center text-sm font-medium text-primary-700"> Embeddable editor for direct mail </span>
          <h1
            class="text-pretty text-4xl font-semibold leading-tight tracking-tight text-gray-950 sm:text-5xl lg:w-[592px] lg:text-[60px] lg:leading-[72px] lg:tracking-[-1.2px]"
          >
            The postcard editor your platform can ship this week.
          </h1>
          <p class="text-pretty text-lg text-gray-800 lg:w-[592px] lg:text-[20px] lg:leading-[30px]">
            Embed a full design editor for postcards and letters. Your users pick a template and personalise it. You get clean, print-ready HTML for
            Lob, PostGrid or your own press.
          </p>
        </div>

        <!-- CTAs -->
        <div class="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row">
          <Link
            href="/signup"
            class="relative flex w-full items-center justify-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-b from-primary-600 to-primary-700 px-[18px] py-3 shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)] sm:w-auto"
          >
            <span
              class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-2px_0px_0px_rgba(16,24,40,0.2)]"
              aria-hidden="true"
            />
            <span class="text-base font-semibold text-white">Try the editor</span>
          </Link>
          <Link
            href="/developers"
            class="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 px-[18px] py-3 shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)] sm:w-auto"
          >
            <span class="text-base font-semibold text-gray-800">See the embed docs</span>
            <svg
              class="size-5 shrink-0"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4.16667 10H15.8333M10 15.8333L15.8333 10L10 4.16667"
                stroke="#344054"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Link>
        </div>

        <!-- Trust signals -->
        <div class="flex flex-wrap gap-x-6 gap-y-2">
          <span
            v-for="trust in trustSignals"
            :key="trust"
            class="flex items-center gap-1 text-sm text-gray-700"
          >
            <svg
              class="size-4 shrink-0 text-primary-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12l5 5L20 7" />
            </svg>
            {{ trust }}
          </span>
        </div>
      </div>

      <!-- Mobile screenshot -->
      <div class="relative w-full max-w-[480px] lg:hidden">
        <img
          :src="AppScreenshot"
          alt="Designer Tool editor screenshot"
          class="w-full rounded-lg border-4 border-[rgba(153,153,153,0.09)] object-cover shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]"
        />
      </div>

      <!-- Mobile logos -->
      <div class="flex w-full items-center justify-center gap-4 lg:hidden">
        <div
          v-for="logo in integrationLogos"
          :key="logo.name"
          class="flex size-[42px] items-center justify-center rounded-xl bg-gradient-to-t from-[rgba(236,236,236,0.8)] to-[rgba(249,249,249,0.8)] p-[9px] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.12)]"
        >
          <img
            :src="logo.src"
            :alt="logo.name"
            class="size-6"
          />
        </div>
      </div>

      <!-- Desktop visual: screenshot + floating logos -->
      <div class="relative hidden shrink-0 overflow-hidden lg:block lg:h-[512px] lg:w-[704px]">
        <!-- Screenshot -->
        <div
          class="absolute left-[166px] top-0 h-[512px] w-[768px] overflow-hidden rounded-[10px] border-4 border-[rgba(153,153,153,0.09)] shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]"
        >
          <img
            :src="AppScreenshot"
            alt="Designer Tool editor screenshot"
            class="size-full object-cover"
          />
        </div>

        <!-- Floating integration icons -->
        <div
          class="absolute left-[84px] top-[25px] flex size-14 items-center justify-center rounded-xl bg-gradient-to-t from-[rgba(236,236,236,0.8)] to-[rgba(249,249,249,0.8)] p-3 shadow-[0px_2px_0px_0px_rgba(0,0,0,0.12)]"
        >
          <img
            :src="DmmLogo"
            alt="Direct Mail Manager"
            class="size-8"
          />
        </div>
        <div
          class="absolute left-[15px] top-[126px] flex size-14 items-center justify-center rounded-xl bg-gradient-to-t from-[rgba(236,236,236,0.8)] to-[rgba(249,249,249,0.8)] p-3 shadow-[0px_2px_0px_0px_rgba(0,0,0,0.12)]"
        >
          <img
            :src="ShopifyLogo"
            alt="Shopify"
            class="size-8"
          />
        </div>
        <div
          class="absolute left-[84px] top-[242px] flex size-14 items-center justify-center rounded-xl bg-gradient-to-t from-[rgba(236,236,236,0.8)] to-[rgba(249,249,249,0.8)] p-3 shadow-[0px_2px_0px_0px_rgba(0,0,0,0.12)]"
        >
          <img
            :src="LobLogo"
            alt="Lob"
            class="size-8"
          />
        </div>
        <div
          class="absolute left-[15px] top-[364px] flex size-14 items-center justify-center rounded-xl bg-gradient-to-t from-[rgba(236,236,236,0.8)] to-[rgba(249,249,249,0.8)] p-3 shadow-[0px_2px_0px_0px_rgba(0,0,0,0.12)]"
        >
          <img
            :src="PostgridLogo"
            alt="PostGrid"
            class="size-8"
          />
        </div>

        <!-- Decorative dots -->
        <div
          class="absolute left-[110px] top-[178px] flex size-5 items-center justify-center"
          aria-hidden="true"
        >
          <div class="size-4 flex-none rotate-[17deg] rounded-[2px] bg-[rgba(255,209,169,0.8)]" />
        </div>
        <div
          class="absolute left-[45px] top-[310px] flex size-5 items-center justify-center"
          aria-hidden="true"
        >
          <div class="bg-primary-200/80 size-4 flex-none rotate-[-21deg] rounded-[2px]" />
        </div>
        <div
          class="absolute left-[27px] top-[64px] flex size-5 items-center justify-center"
          aria-hidden="true"
        >
          <div class="bg-primary-200/80 size-4 flex-none rotate-[-21deg] rounded-[2px]" />
        </div>
        <div
          class="absolute left-[123px] top-[436px] flex size-5 items-center justify-center"
          aria-hidden="true"
        >
          <div class="size-4 flex-none rotate-[25deg] rounded-[2px] bg-[rgba(255,209,169,0.8)]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import AppScreenshot from "@/../images/app-screenshot.png";
import DmmLogo from "@/../images/integration-icons/dmm-logo.svg";
import ShopifyLogo from "@/../images/integration-icons/shopify-icon.svg";
import LobLogo from "@/../images/integration-icons/lob-icon.svg";
import PostgridLogo from "@/../images/integration-icons/postgrid-icon.svg";

const trustSignals = ["No account needed to try", "Free sandbox for developers", "No per-postcard fee"];

const integrationLogos = [
  { name: "Direct Mail Manager", src: DmmLogo },
  { name: "Shopify", src: ShopifyLogo },
  { name: "Lob", src: LobLogo },
  { name: "PostGrid", src: PostgridLogo },
];
</script>
